import Permission, { PermissionName } from '@constants/permission'
import Role, { RoleName } from '@constants/role'
import useSelector from '@hooks/use-selector'
import { ReactNode } from 'react'
import {
  selectCurrentPermissionNames,
  selectCurrentRoles,
  selectCurrentStoreId,
} from '@store/selectors'

/** Route params arrive as strings, but most callers already hold a number. */
type StoreId = number | string

const toStoreId = (storeId: StoreId): number =>
  typeof storeId === 'string' ? parseInt(storeId, 10) : storeId

export const renderIfPermitted = (
  element: ReactNode,
  neededPermission?: PermissionName,
): ReactNode => {
  if (neededPermission && !hasPerms(neededPermission)) {
    return null
  }

  return element
}

export const hasRole = (neededRole: RoleName): boolean => {
  return hasAnyRole([neededRole])
}

export const hasAnyRole = (desiredRoles: RoleName[]): boolean => {
  const roles = useSelector(selectCurrentRoles)

  if (roles?.includes(Role.ADMIN) || roles?.includes(Role.OWNER)) return true

  return desiredRoles.some((role) => roles?.includes(role))
}

export const hasPerms = (neededPermission: PermissionName): boolean => {
  const permissions = useSelector(selectCurrentPermissionNames)

  return checkPerms(permissions, neededPermission)
}

export const hasPermsAtStore = (
  neededPermission: PermissionName,
  storeId: StoreId,
): boolean => {
  const permissions = useSelector(selectCurrentPermissionNames)
  const storeIds = useSelector(selectCurrentStoreId)

  return checkPermsAtStore(permissions, neededPermission, storeIds, storeId)
}

export const checkPerms = (
  permissions: string[] | undefined,
  neededPermission: PermissionName,
): boolean => {
  return Boolean(permissions?.includes(neededPermission))
}

export const checkPermsAtStore = (
  permissions: string[] | undefined,
  neededPermission: PermissionName,
  storeIds: number[] | undefined,
  storeId: StoreId,
): boolean => {
  return Boolean(
    permissions?.includes(neededPermission) &&
      (permissions?.includes(Permission.STORES_UPDATE_ALL) ||
        storeIds?.includes(toStoreId(storeId))),
  )
}

export default hasPerms
